import type { ProfileRole, ProfilePermissions } from '../types/account'

/**
 * Role-based permissions configuration for profile collaboration
 */
export const ROLE_PERMISSIONS: Record<ProfileRole, ProfilePermissions> = {
  owner: {
    viewProfile: true,
    editProfile: true,
    viewReports: true,
    shareProfile: true,
    manageAccess: true
  },
  editor: {
    viewProfile: true,
    editProfile: true,
    viewReports: true,
    shareProfile: false,
    manageAccess: false
  },
  viewer: {
    viewProfile: true,
    editProfile: false,
    viewReports: true,
    shareProfile: false,
    manageAccess: false
  },
  restricted_viewer: {
    viewProfile: true,
    editProfile: false,
    viewReports: false, // Can see profile but not genetic reports
    shareProfile: false,
    manageAccess: false
  },
  accountability_partner: {
    viewProfile: true,
    editProfile: false,
    viewReports: false,
    shareProfile: false,
    manageAccess: false
  }
}

/**
 * Human-readable role descriptions
 */
export const ROLE_DESCRIPTIONS: Record<ProfileRole, string> = {
  owner: 'Full access to view, edit, and manage the profile including sharing with others',
  editor: 'Can view and edit profile information and reports, but cannot share or manage access',
  viewer: 'Can view profile information and reports, but cannot make changes',
  restricted_viewer: 'Can view basic profile information only, no access to genetic reports',
  accountability_partner: 'Can view shared wellness goal progress only, granted automatically via the partnership flow'
}

/**
 * Role display names for UI
 */
export const ROLE_DISPLAY_NAMES: Record<ProfileRole, string> = {
  owner: 'Owner',
  editor: 'Editor',
  viewer: 'Viewer',
  restricted_viewer: 'Limited Viewer',
  accountability_partner: 'Goals Partner'
}

/**
 * Default invitation expiry time (7 days)
 */
export const DEFAULT_INVITATION_EXPIRY_DAYS = 7

/**
 * Maximum number of collaborators per profile
 */
export const MAX_COLLABORATORS_PER_PROFILE = 50

/**
 * Activity log retention period (90 days)
 */
export const ACTIVITY_LOG_RETENTION_DAYS = 90

/**
 * Helper function to check if a role has a specific permission
 */
export const hasPermission = (role: ProfileRole, permission: keyof ProfilePermissions): boolean => {
  return ROLE_PERMISSIONS[role][permission]
}

/**
 * Helper function to get all available roles for sharing
 */
export const getShareableRoles = (): ProfileRole[] => {
  return ['editor', 'viewer', 'restricted_viewer']
}

/**
 * Helper function to validate role
 */
export const isValidRole = (role: string): role is ProfileRole => {
  return ['owner', 'editor', 'viewer', 'restricted_viewer'].includes(role)
}

/**
 * Helper function to determine if one role can grant another role
 */
export const canGrantRole = (granterRole: ProfileRole, targetRole: ProfileRole): boolean => {
  // Only owners can grant roles
  if (granterRole !== 'owner') return false

  // Owners cannot grant owner role (only one owner per profile)
  if (targetRole === 'owner') return false

  // accountability_partner is internal-only — set solely by the partnership
  // accept/end lifecycle, never through the manual "invite a collaborator" UI.
  if (targetRole === 'accountability_partner') return false

  return true
}

/**
 * Helper function to get role hierarchy level (lower number = more access)
 */
export const getRoleLevel = (role: ProfileRole): number => {
  const levels: Record<ProfileRole, number> = {
    owner: 0,
    editor: 1,
    viewer: 2,
    restricted_viewer: 3,
    accountability_partner: 4
  }
  return levels[role]
}

/**
 * Helper function to check if role A has equal or greater permissions than role B
 */
export const hasEqualOrGreaterAccess = (roleA: ProfileRole, roleB: ProfileRole): boolean => {
  return getRoleLevel(roleA) <= getRoleLevel(roleB)
} 